import React, { useRef, useState } from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * SpeakButton — reads text aloud using backend TTS.
 */
export default function SpeakButton({ text, testId = "speak-btn" }) {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  };

  const play = async () => {
    if (!text || !text.trim()) return;
    if (playing) return stop();
    setBusy(true);
    try {
      const resp = await api.post(
        "/voice/tts",
        { text },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => {
        setPlaying(false);
        URL.revokeObjectURL(url);
      };
      a.onerror = () => {
        setPlaying(false);
        toast.error("Could not play audio.");
      };
      setPlaying(true);
      await a.play();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) toast.info("Voice output is not turned on yet.");
      else toast.error("Voice output is not available right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={play}
      disabled={busy || !text}
      className="tm-btn tm-btn-ghost"
      data-testid={testId}
      aria-label={playing ? "Stop reading" : "Read aloud"}
    >
      {busy ? (
        <Loader2 size={18} className="animate-spin" />
      ) : playing ? (
        <Square size={18} />
      ) : (
        <Volume2 size={18} />
      )}
      <span className="hidden sm:inline">{playing ? "Stop" : "Read aloud"}</span>
    </button>
  );
}
